import React, { useState, useEffect } from 'react'
import { Project } from '../../types'
import { X, Clock, Calendar, FileText, AlertCircle, PlusCircle } from 'lucide-react'
import toast from 'react-hot-toast'

interface ManualTimeEntryModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: ManualTimeEntryData) => Promise<void>
  projects: Project[]
  isLoading?: boolean
}

export interface ManualTimeEntryData {
  projectId: string
  description: string
  startTime: string
  endTime: string
  date: string
  manualReason: string
}

const ManualTimeEntryModal: React.FC<ManualTimeEntryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  projects,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<ManualTimeEntryData>({
    projectId: '',
    description: '',
    startTime: '09:00',
    endTime: '17:00',
    date: new Date().toISOString().split('T')[0],
    manualReason: ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [calculatedDuration, setCalculatedDuration] = useState<string>('')

  // 計算工時
  useEffect(() => {
    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}:00`)
      const end = new Date(`2000-01-01T${formData.endTime}:00`)
      
      if (end > start) {
        const diffMs = end.getTime() - start.getTime()
        const hours = Math.floor(diffMs / (1000 * 60 * 60))
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
        setCalculatedDuration(`${hours}h ${minutes}m`)
      } else {
        setCalculatedDuration('無效時間')
      }
    }
  }, [formData.startTime, formData.endTime])

  // 表單驗證
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.projectId) {
      newErrors.projectId = '請選擇專案'
    }

    if (!formData.date) {
      newErrors.date = '請選擇日期'
    }

    if (!formData.startTime) {
      newErrors.startTime = '請選擇開始時間'
    }

    if (!formData.endTime) {
      newErrors.endTime = '請選擇結束時間'
    }

    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}:00`)
      const end = new Date(`2000-01-01T${formData.endTime}:00`)
      
      if (end <= start) {
        newErrors.endTime = '結束時間必須晚於開始時間'
      }
    }

    if (!formData.manualReason.trim()) {
      newErrors.manualReason = '請說明手動新增的原因'
    }

    const selectedDate = new Date(formData.date)
    const today = new Date()
    today.setHours(23, 59, 59, 999)
    
    if (selectedDate > today) {
      newErrors.date = '不能選擇未來的日期'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // 處理表單提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      await onSubmit(formData)
      handleClose()
      toast.success('手動時間記錄已新增')
    } catch (error) {
      toast.error('新增時間記錄失敗')
    }
  }

  // 處理關閉
  const handleClose = () => {
    setFormData({
      projectId: '',
      description: '',
      startTime: '09:00',
      endTime: '17:00',
      date: new Date().toISOString().split('T')[0],
      manualReason: ''
    })
    setErrors({})
    setCalculatedDuration('')
    onClose()
  }

  // 處理輸入變更
  const handleInputChange = (field: keyof ManualTimeEntryData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 z-50 animate-fade-in">
      <div className="glass-card w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl
        max-h-[95vh] sm:max-h-[90vh] overflow-hidden animate-scale-in flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 sm:p-6 border-b border-secondary-200/50
          bg-gradient-to-r from-primary-50 to-amber-50/50 flex-shrink-0">
          {/* 手機版頂部拖曳指示條 */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-secondary-300 sm:hidden" />
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-primary-100 flex items-center justify-center">
              <PlusCircle className="w-4.5 h-4.5 sm:w-5 sm:h-5 text-primary-600" />
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-secondary-800">手動新增記錄</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-secondary-400 hover:text-secondary-600
              hover:bg-secondary-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-4 py-4 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto flex-1 min-h-0">
          {/* 專案選擇 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5 sm:mb-2">
              專案選擇 <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.projectId}
              onChange={(e) => handleInputChange('projectId', e.target.value)}
              className={`input-modern text-base ${errors.projectId ? 'border-red-400 focus:border-red-500' : ''}`}
            >
              <option value="">選擇專案...</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            {errors.projectId && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {errors.projectId}
              </p>
            )}
          </div>

          {/* 工作描述 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5 sm:mb-2">
              工作描述
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="輸入工作內容..."
              className="input-modern resize-none text-base"
              rows={2}
            />
          </div>

          {/* 日期選擇 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5 sm:mb-2 flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-primary-500" />
              日期選擇 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className={`input-modern text-base ${errors.date ? 'border-red-400 focus:border-red-500' : ''}`}
            />
            {errors.date && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {errors.date}
              </p>
            )}
          </div>

          {/* 時間設定 */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1.5 sm:mb-2">
                開始時間 <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                className={`input-modern text-base ${errors.startTime ? 'border-red-400 focus:border-red-500' : ''}`}
              />
              {errors.startTime && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {errors.startTime}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1.5 sm:mb-2">
                結束時間 <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                className={`input-modern text-base ${errors.endTime ? 'border-red-400 focus:border-red-500' : ''}`}
              />
              {errors.endTime && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {errors.endTime}
                </p>
              )}
            </div>
          </div>

          {/* 計算工時顯示 */}
          {calculatedDuration && (
            <div className="flex items-center gap-2.5 sm:gap-3 p-3 sm:p-4 rounded-xl
              bg-gradient-to-r from-blue-50 to-indigo-50/50 border border-blue-200/50">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4.5 h-4.5 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-xs text-blue-600 font-medium">計算工時</div>
                <div className="text-base sm:text-lg font-bold text-blue-700 font-mono">{calculatedDuration}</div>
              </div>
            </div>
          )}

          {/* 手動新增原因 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5 sm:mb-2 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-primary-500" />
              新增原因 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.manualReason}
              onChange={(e) => handleInputChange('manualReason', e.target.value)}
              placeholder="說明為什麼要手動新增這筆記錄..."
              className={`input-modern resize-none text-base ${errors.manualReason ? 'border-red-400 focus:border-red-500' : ''}`}
              rows={2}
            />
            {errors.manualReason && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {errors.manualReason}
              </p>
            )}
          </div>

          {/* 提示訊息 */}
          <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50/50
            border border-amber-200/50">
            <div className="flex items-start gap-2.5 sm:gap-3">
              <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-amber-100
                flex items-center justify-center">
                <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" />
              </div>
              <div className="text-xs sm:text-sm text-amber-800">
                <p className="font-medium mb-0.5 sm:mb-1">注意事項</p>
                <ul className="space-y-0.5 text-amber-700">
                  <li>• 手動新增的記錄將標記為「手動新增」</li>
                  <li>• 請確保時間設定正確，避免重複記錄</li>
                </ul>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex gap-2.5 sm:gap-3 px-4 py-3 sm:p-6 border-t border-secondary-200/50 bg-secondary-50/30 flex-shrink-0
          pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-6">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl font-medium
              text-secondary-700 bg-white border border-secondary-200
              hover:bg-secondary-50 active:bg-secondary-100 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex-1 btn-modern-primary disabled:opacity-50 disabled:cursor-not-allowed
              active:scale-[0.98] transition-transform"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                新增中...
              </span>
            ) : (
              '確認新增'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ManualTimeEntryModal
